import React, { createContext, useContext, useMemo } from "react";
import { useVideoConfig } from "remotion";

/**
 * Output formats of the B2C video. Same timeline everywhere; only the
 * layout adapts.
 *
 *   square     1080×1080  landing "3 steps" embed (the validated master)
 *   portrait   1080×1920  Reels / TikTok / Shorts / Stories
 *   appstore    886×1920  App Store preview (iPhone 6.5"/6.7")
 *   landscape  1920×1080  web / YouTube / X
 */
export type B2CFormat = "square" | "portrait" | "appstore" | "landscape";

export const B2C_SIZES: Record<B2CFormat, { width: number; height: number }> = {
  square: { width: 1080, height: 1080 },
  portrait: { width: 1080, height: 1920 },
  appstore: { width: 886, height: 1920 },
  landscape: { width: 1920, height: 1080 },
};

/** Scenes are authored on a stage whose short side is always 1080. */
const UNIT = 1080;

export type Stage = {
  /** Logical stage size (short side = 1080). */
  w: number;
  h: number;
  /** Layout family: scenes branch on this, never on the raw format. */
  shape: "square" | "tall" | "wide";
  /**
   * Keep text out of these bands (logical px). Tall formats reserve room
   * for the Reels/TikTok UI (profile row on top, caption + buttons below).
   */
  safeTop: number;
  safeBottom: number;
};

const StageContext = createContext<Stage | null>(null);

export const useStage = (): Stage => {
  const stage = useContext(StageContext);
  if (!stage) throw new Error("useStage() must be used inside <StageFrame>.");
  return stage;
};

/**
 * Lays out a logical stage (short side 1080) and scales it to the real
 * composition size, so a 886-wide App Store cut is the portrait layout at
 * 0.82× instead of a third set of pixel values. Square renders unscaled.
 */
export const StageFrame: React.FC<{ format: B2CFormat; children: React.ReactNode }> = ({
  format,
  children,
}) => {
  const { width, height } = useVideoConfig();
  const k = Math.min(width, height) / UNIT;
  const w = width / k;
  const h = height / k;

  const stage = useMemo<Stage>(() => {
    const shape = w === h ? "square" : w > h ? "wide" : "tall";
    const social = format === "portrait";
    return {
      w,
      h,
      shape,
      safeTop: shape === "tall" ? (social ? 200 : 140) : 0,
      safeBottom: shape === "tall" ? (social ? 300 : 140) : 0,
    };
  }, [w, h, format]);

  return (
    <StageContext.Provider value={stage}>
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: w,
          height: h,
          transform: k === 1 ? undefined : `scale(${k})`,
          transformOrigin: "0 0",
        }}
      >
        {children}
      </div>
    </StageContext.Provider>
  );
};
